// ============================================================================
// Safe Care - Validation des demandes de souscription (côté agent/validateur)
// Module : features/assistance_sociale/pages
// Route : /souscription/validation (ex onglet "Validation Souscription" de
//         /assurance/validation)
// Charte : .tab / .formTitle / .bG / .bt_add / .tabItem / .tabElement / InputForm / SearchInput
// Pattern : identique à Garantie.tsx (formulaire dans Tab + tableau en dessous)
// ============================================================================

import { useState, useEffect } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Modal, Input } from 'antd';
import type { TabsProps } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import InputForm from '../../../Components/InputT';
import SearchInput from '../../../Components/SearchInput';
import SearchModal from '../../../Components/SearchModal';
import Params from '../../../Types/ParamsPicklist';
import {
  useGetAllDemandesSouscription,
  useValiderDemandeSouscription,
  useRejeterDemandeSouscription,
  useApercuFrais,
} from '../hooks';
import { DemandeSouscription } from '../types';

const { TextArea } = Input;

// Paramètres de recherche pour les picklists
let searchDemande: Params = {
  CodeAgence: '',
  Table: 'DEMANDESOUSCRIPTION',
  Colonne1: 'IdDemande',
  Colonne2: 'NOMCLI',
  Colagence: '101',
  Choix: 22,
};

// Picklist client (table CLIENT existante) pour le filtre du tableau
let searchClient: Params = {
  CodeAgence: '',
  Table: 'CLIENT',
  Colonne1: 'NUMCLI',
  Colonne2: 'NOMCLI',
  Colagence: '101',
  Choix: 22,
};

const ValidationDemandesSouscription = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const getUserCode = () => {
    const u = auth();
    const raw = u?.coduti ?? (u as any)?.CodUti ?? (u as any)?.CODUTI ?? localStorage.getItem('codUti') ?? sessionStorage.getItem('codUti') ?? '';
    const cleaned = String(raw).trim();
    if (!cleaned || cleaned.toLowerCase() === 'undefined' || cleaned.toLowerCase() === 'null') return '';
    return cleaned;
  };

  const { data: demandes, isLoading, refetch } = useGetAllDemandesSouscription('EN_ATTENTE');
  const valider = useValiderDemandeSouscription();
  const rejeter = useRejeterDemandeSouscription();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [Data, SetData] = useState<DemandeSouscription[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedDemande, setSelectedDemande] = useState<DemandeSouscription | null>(null);
  const [modalRejet, setModalRejet] = useState(false);
  const [motifRejet, setMotifRejet] = useState('');
  const [activeTab, setActiveTab] = useState('1');

  // Frais de souscription débités automatiquement à la validation (grille)
  const { data: apercuFraisRes } = useApercuFrais('SOUSCRIPTION', { idVersion: selectedDemande?.IdVersion });

  // ---------- Filtre par client (picklist au-dessus du tableau) ----------
  const [formFiltre] = Form.useForm();
  const [filtreClient, setFiltreClient] = useState<{ numcli: string; libelle: string } | null>(null);

  // Etats des boutons d'action (charte cSAL)
  const [ajoutActve, setAjoutActif] = useState(false);
  const [modifActve, setModifActve] = useState(true);
  const [enregActve, setEnregActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [impActive, setImpActive] = useState(true);
  const [actuActive, setActActive] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);
  const [consulActive, setConsulActive] = useState(false);

  useEffect(() => {
    if (demandes) SetData(demandes);
  }, [demandes]);

  useEffect(() => {
    // Force un rechargement à l'ouverture de la page pour ne pas afficher le cache vide
    refetch();
  }, []);

  function initialiazeButton() {
    setAjoutActif(false);
    setModifActve(true);
    setEnregActve(true);
    setSupActive(true);
    setImpActive(true);
    setActActive(true);
    setAnnulActive(true);
    setConsulActive(false);
  }

  const onSelectChange = (newSelectedRowKeys: any, selectedRows: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const record = selectedRows?.[0] ?? Data.find((d: any) => d.IdDemande === newSelectedRowKeys?.[0]);
    if (record) {
      form.setFieldsValue(record);
      setSelectedDemande(record);
    }
    setEnregActve(false);
    setSupActive(false);
    setAnnulActive(false);
    setConsulActive(true);
    setImpActive(false);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  function consult() {
    if (selectedDemande) {
      form.setFieldsValue(selectedDemande);
    }
    setAnnulActive(false);
    setModifActve(false);
    setEnregActve(true);
  }

  function handleAnulle() {
    form.resetFields();
    initialiazeButton();
    setSelectedRowKeys([]);
    setSelectedDemande(null);
  }

  function actualiser() {
    setLoading(true);
    refetch().then(() => {
      setLoading(false);
      SweetAlert.fire({ title: 'Liste actualisée', icon: 'success', timer: 1000 });
    });
  }

  const handleValider = () => {
    const codeUtilisateur = getUserCode();
    console.log('[DEBUG FRONT] getUserCode=', codeUtilisateur);
    if (!codeUtilisateur) {
      SweetAlert.fire({ title: 'Session invalide', text: 'Impossible de récupérer le code utilisateur.', icon: 'error' });
      return;
    }
    const record = Data.find((d) => d.IdDemande === selectedRowKeys[0]);
    if (!record) {
      SweetAlert.fire({ title: 'Sélectionnez une demande', icon: 'warning' });
      return;
    }
    if ((record.UtiSai ?? '').trim().toUpperCase() === codeUtilisateur.toUpperCase()) {
      SweetAlert.fire({ title: 'Validation impossible', text: 'Vous ne pouvez pas valider une demande que vous avez vous-même saisie.', icon: 'warning' });
      return;
    }
    const frais = apercuFraisRes?.StatutCode === 200 ? Number(apercuFraisRes.Result?.Table1?.[0]?.Frais) || 0 : 0;
    SweetAlert.fire({
      title: 'Validation',
      text: `Valider la demande de ${record.NOMCLI} ${record.PRECLI} pour ${record.NomProduit} ?`
        + (frais > 0 ? ` Les frais de souscription (${frais.toLocaleString('fr-FR')} FCFA) seront débités du compte ${record.NumCptDebit}.` : '')
        + (apercuFraisRes && apercuFraisRes.StatutCode !== 200 ? ` Attention : ${apercuFraisRes.Message}` : ''),
      showCancelButton: true,
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
      color: '#116599',
      confirmButtonColor: '#116599',
      cancelButtonColor: 'darkred',
      icon: 'question',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        valider.mutate(
          { idDemande: record.IdDemande!, utiVal: codeUtilisateur },
          {
            onSuccess: (res) => {
              setLoading(false);
              if (res?.StatutCode === 200) {
                SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
                handleAnulle();
              } else {
                SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
              }
            },
          }
        );
      }
    });
  };

  const handleRejeter = () => {
    const codeUtilisateur = getUserCode();
    if (!codeUtilisateur) {
      SweetAlert.fire({ title: 'Session invalide', text: 'Impossible de récupérer le code utilisateur.', icon: 'error' });
      return;
    }
    const record = Data.find((d) => d.IdDemande === selectedRowKeys[0]);
    if (!record) {
      SweetAlert.fire({ title: 'Sélectionnez une demande', icon: 'warning' });
      return;
    }
    setSelectedDemande(record);
    setMotifRejet('');
    setModalRejet(true);
  };

  const confirmRejet = () => {
    if (!selectedDemande) return;
    const codeUtilisateur = getUserCode();
    if (!codeUtilisateur) {
      SweetAlert.fire({ title: 'Session invalide', text: 'Impossible de récupérer le code utilisateur.', icon: 'error' });
      return;
    }
    setLoading(true);
    rejeter.mutate(
      {
        idDemande: selectedDemande.IdDemande!,
        motifRejet,
        utiVal: codeUtilisateur,
      },
      {
        onSuccess: (res) => {
          setLoading(false);
          setModalRejet(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            handleAnulle();
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
      }
    );
  };

  // Sélection d'un client via la picklist standard (code + libellé)
  const handleFiltreClient = (code: string, libelle: string) => {
    setFiltreClient(code ? { numcli: code, libelle } : null);
  };

  // Tableau : filtré sur le client choisi dans la picklist
  const dataFiltree = filtreClient ? Data.filter((d) => d.Numcli === filtreClient.numcli) : Data;

  const columns: ColumnsType<DemandeSouscription> = [
    { title: 'Client', dataIndex: 'NOMCLI', width: 120 },
    { title: 'Prénom', dataIndex: 'PRECLI', width: 120 },
    { title: 'Produit', dataIndex: 'NomProduit', width: 150 },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100 },
    { title: 'Date souhaitée', dataIndex: 'DateEffetSouhaitee', width: 120 },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
    { title: 'Saisi par', dataIndex: 'UtiSai', width: 90 },
  ];

  // ==================== ONGLET FORMULAIRE DEMANDE (Tab1) ====================
  // Ce tab contient le formulaire avec picklists, comme Tab1 dans Garantie.tsx
  const FormTab = () => {
    return (
      <div className="tabItem">
        <div>
          <Box fontStyle="inherit" className="border border-left" width="85%" position="absolute" sx={{ p: 1 }}>
            <legend>Validation souscription</legend>
            <Box display="flex" fontStyle="inherit" sx={{ mb: 1 }}>
              <Box fontStyle="inherit">
                <Form.Item name="IdDemande" hidden>
                  <input type="hidden" />
                </Form.Item>
                <SearchInput
                  width={9}
                  form={form}
                  Titre="Recherche Demande"
                  label="N° Demande"
                  name="IdDemande"
                  Parametre={searchDemande}
                />
                <InputForm width={9} label="Client" name="NOMCLI" type="label" />
                <InputForm width={9} label="Prénom" name="PRECLI" type="label" />
                <InputForm width={9} label="Produit" name="NomProduit" type="label" />
              </Box>
              <hr />
              <Box fontStyle="inherit">
                <InputForm width={9} label="Version" name="NumeroVersion" type="label" />
                <InputForm width={9} label="Cotisation" name="MontantCotisation" type="label" />
                <InputForm width={9} label="Date souhaitée" name="DateEffetSouhaitee" type="label" />
                <InputForm width={9} label="Statut" name="Statut" type="label" />
              </Box>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Validation Souscription',
      children: FormTab(),
    },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>{window.location.pathname}</b>
      </Typography>
      <Spin spinning={isLoading || loading} size="large" style={{ color: '#116599' }}>
        <Form
          disabled={!ajoutActve && !consulActive}
          autoComplete="off"
          labelAlign="left"
          form={form}
          onFinish={() => {}}
          onFinishFailed={() => {}}
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          {activeTab === '1' && (
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={consulActive}
                  onClick={consult}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Consulter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={enregActve}
                  onClick={handleValider}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Valider
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={supActive}
                  onClick={handleRejeter}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Rejeter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={impActive}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Imprimer
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={actuActive}
                  onClick={actualiser}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Actualiser
                </Button>
                <Button
                  size="large"
                  disabled={annulActive}
                  onClick={handleAnulle}
                  className="bt_add"
                  type="ghost"
                  htmlType="reset"
                  block
                >
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          )}

          <div className="clientP">
            <div>
              <Tabs
                size="small"
                type="card"
                activeKey={activeTab}
                onChange={setActiveTab}
                defaultActiveKey="1"
                items={items}
                animated={{ inkBar: true, tabPane: true }}
              />
            </div>
          </div>
        </Form>
        <br />
        <br />
        {/* Tableau en dessous du formulaire (hors Form pour permettre le clic radio) */}
        {activeTab === '1' && (
          <Box fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 5, display: 'block' }}>
            {/* Filtre : picklist client (composant standard SearchModal, table CLIENT) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Form form={formFiltre} component={false}>
                <SearchModal
                  width={12}
                  notRequired
                  Titre=""
                  label="Filtrer par client"
                  name="NumcliFiltre"
                  Parametre={searchClient}
                  Taille1={167}
                  Taille2={320}
                  form={formFiltre}
                  handleFunction={handleFiltreClient}
                />
              </Form>
              {filtreClient && (
                <Button
                  size="small"
                  type="link"
                  danger
                  onClick={() => { formFiltre.resetFields(); setFiltreClient(null); }}
                >
                  Effacer le filtre
                </Button>
              )}
              {filtreClient && (
                <Typography variant="caption" color="textSecondary">
                  {dataFiltree.length} demande(s)
                </Typography>
              )}
            </Box>
            <Spin spinning={isLoading || loading}>
              <div>
                <Table
                  size="small"
                  style={{ width: '100%' }}
                  rowKey="IdDemande"
                  rowSelection={{ ...rowSelection, type: 'radio' }}
                  columns={columns}
                  dataSource={dataFiltree}
                  scroll={{ y: 250, x: 10 }}
                  pagination={{ defaultPageSize: 5, position: ['bottomRight'] }}
                />
              </div>
            </Spin>
          </Box>
        )}
      </Spin>

      <Modal
        title="Motif du rejet"
        open={modalRejet}
        onOk={confirmRejet}
        onCancel={() => setModalRejet(false)}
        okText="Confirmer le rejet"
        cancelText="Annuler"
        okButtonProps={{ danger: true, loading: loading }}
        className="safeCareModal"
      >
        <TextArea
          rows={4}
          value={motifRejet}
          onChange={(e) => setMotifRejet(e.target.value)}
          placeholder="Indiquez le motif du rejet..."
        />
      </Modal>
    </div>
  );
};

export default ValidationDemandesSouscription;